import { useMutation } from '@apollo/client';
import type { UpdateAggregationSettingsForOrgMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateAggregationSettingsForOrgDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type UpdateAggregationSettingsForOrgInput,
  useUpdateAggregationSettingsForOrgTRPC,
} from './useUpdateAggregationSettingsForOrgTRPC';

/**
 * Flag-gated wrapper for the `updateAggregationSettingsForOrg` mutation.
 * Defaults to the legacy Apollo mutation and delegates to the tRPC
 * variant when the `trpc` feature flag is enabled.
 *
 * The public input shape keeps `AppetiteCascadingModel` so existing
 * call sites don't need to change beyond the import swap. The tRPC
 * twin translates this to its `Appetite` field internally.
 */
export const useUpdateAggregationSettingsForOrg = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateAggregationSettingsForOrgDocument,
    {
      update: (cache) => {
        evictField(cache, 'aggregation_org');
      },
    }
  );

  const trpcMutation = useUpdateAggregationSettingsForOrgTRPC();

  const updateAggregationSettingsForOrg = async (
    input: UpdateAggregationSettingsForOrgInput
  ): Promise<UpdateAggregationSettingsForOrgMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateAggregationSettingsForOrg(input);
    }

    const result = await updateGraphQL({
      variables: {
        RiskScoringModel: input.RiskScoringModel ?? null,
        AppetiteCascadingModel: input.AppetiteCascadingModel ?? null,
        Config: input.Config ?? null,
      },
    });
    if (!result.data) {
      throw new Error('Failed to update aggregation settings');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateAggregationSettingsForOrg,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateAggregationSettingsForOrg,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
